import { HttpClient } from '@angular/common/http';
import { afterNextRender, inject, Injector, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Capacitor } from '@capacitor/core';
import { catchError, EMPTY, map, switchMap, take, tap } from 'rxjs';
import { build } from '@build';
import { deviceActions } from '@app/store/device/actions/device.actions';
import { scannerActions } from '@app/store/scanner/actions/scanner.actions';
import { MetricEventDto, MetricsBatchDto } from './metrics.dto';
import { normalizePath } from './normalize-path';

export const METRICS_PATH = 'metrics';

const FLUSH_COUNT = 25;
const FLUSH_MIN_INTERVAL = 60_000;
// The API rejects a batch over 500 events with 422 and stores none of it.
const MAX_BATCH = 500;

/** Buffers client performance events (API timings, render timings, long tasks) and flushes them as fire-and-forget batches to `POST metrics`. */
@Service()
export class MetricsService {
    private readonly httpClient = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly actions = inject(Actions);
    private readonly injector = inject(Injector);

    private readonly events: MetricEventDto[] = [];
    private navigationStart = 0;
    private lastFlush = 0;

    constructor() {
        this.observeLongTasks();
        this.observeNavigations();
        this.observeScans();
        this.observeLock();
    }

    currentRoute(): string {
        return normalizePath(this.router.url);
    }

    record(event: MetricEventDto): void {
        this.events.push(event);
        if (this.events.length >= MAX_BATCH || (this.events.length >= FLUSH_COUNT && performance.now() - this.lastFlush >= FLUSH_MIN_INTERVAL)) {
            this.flush();
        }
    }

    private flush(): void {
        if (this.events.length === 0) {
            return;
        }

        this.lastFlush = performance.now();

        const batch: MetricsBatchDto = {
            version: build.version,
            platform: Capacitor.isNativePlatform() ? 'android' : 'web',
            events: this.events.splice(0),
        };

        this.httpClient
            .skipErrorHandler()
            .skipErrorMessageInterceptor()
            .post<void>(METRICS_PATH, batch)
            .pipe(catchError(() => EMPTY))
            .subscribe();
    }

    private observeLongTasks(): void {
        const observer = new PerformanceObserver((list) => {
            for (const entry of list.getEntries()) {
                this.record({ type: 'longtask', at: Math.round(performance.now()), route: this.currentRoute(), duration: Math.round(entry.duration) });
            }
        });

        // `{ type, buffered }` is Chrome 77, and an entryTypes list the WebView rejects throws rather than warning.
        try {
            observer.observe({ entryTypes: ['longtask'] });
        } catch {
            observer.disconnect();
        }
    }

    private observeNavigations(): void {
        this.router.events
            .pipe(
                tap((event) => {
                    if (event instanceof NavigationStart) {
                        this.navigationStart = performance.now();
                    } else if (event instanceof NavigationEnd) {
                        this.recordRender('navigation', this.navigationStart);
                    }
                }),
                takeUntilDestroyed(),
            )
            .subscribe();
    }

    private observeScans(): void {
        this.actions
            .pipe(
                ofType(scannerActions.scanned),
                map(() => performance.now()),
                switchMap((start) =>
                    this.actions.pipe(
                        ofType(scannerActions.scanResolved),
                        take(1),
                        map(() => start),
                    ),
                ),
                tap((start) => this.recordRender('scan', start)),
                takeUntilDestroyed(),
            )
            .subscribe();
    }

    private observeLock(): void {
        this.actions
            .pipe(
                ofType(deviceActions.lock),
                tap(() => this.flush()),
                takeUntilDestroyed(),
            )
            .subscribe();
    }

    private recordRender(name: 'scan' | 'navigation', start: number): void {
        afterNextRender(
            () => {
                const now = performance.now();
                this.record({ type: 'render', name, at: Math.round(now), route: this.currentRoute(), duration: Math.round(now - start) });
            },
            {
                injector: this.injector,
            },
        );
    }
}
